import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, teamMembers, brandStory, contactInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9tfo5tca23r0tr1ai20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于小蓝视频 - 轻量高清短视频平台',
          description: '了解小蓝视频的品牌故事、团队介绍和联系方式',
          url: 'https://kbhkgkg.cn/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '小蓝视频',
            url: 'https://kbhkgkg.cn',
          },
        })}
      </script>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Story */}
          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8 text-center">
              {brandStory.title}
            </h1>
            <div className="max-w-3xl mx-auto space-y-4">
              {brandStory.paragraphs.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>

          {/* Team */}
          <section className="mb-16 bg-background-100/50 rounded-lg p-8 md:p-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member, i) => (
                <div key={i} className="text-center">
                  <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-3-line text-accent-600 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-primary-500 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="bg-background-100/50 rounded-lg p-8 md:p-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500">{contactInfo.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服电话</div>
                      <div className="text-sm text-foreground-500">{contactInfo.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-500">{contactInfo.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">{contactInfo.workHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
                      <i className="ri-checkbox-circle-fill mr-1.5"></i>
                      留言提交成功！我们会尽快回复您。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
                      <i className="ri-error-warning-fill mr-1.5"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}